import React, { Fragment, useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Card } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import Select from "react-select";
import SignatureModal from "../Common/SignatureModal";

const IndentAdd = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState("");
  const [units, setUnits] = useState([]);
  const [searchParams] = useSearchParams();

  const projectFromUrl = searchParams.get("project");
  const workOrderFromUrl = searchParams.get("workOrder");
  const [form, setForm] = useState({
    indentNumber: "",
    project: "",
    status: "Pending",
    remarks: "",
    requiredByDate: "",
  });

  useEffect(() => {
    const fetchAllWorkOrders = async () => {
      try {
        const res = await api.get("/work-orders?limit=1000");
        if (res.data.success) {
          setWorkOrders(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch work orders:", err);
      }
    };
    fetchAllWorkOrders();
  }, []);

  const filteredWorkOrders = useMemo(() => {
    if (!form.project) return workOrders;
    return workOrders.filter((wo) => {
      const pid = wo.project?._id || wo.project;
      return String(pid) === String(form.project);
    });
  }, [workOrders, form.project]);

  const handleProjectChange = (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, project: val }));
    if (val && selectedWorkOrder) {
      const currentWo = workOrders.find((w) => String(w._id) === String(selectedWorkOrder));
      const currentWoProjId = currentWo?.project?._id || currentWo?.project;
      if (currentWoProjId && String(currentWoProjId) !== String(val)) {
        setSelectedWorkOrder("");
        setBoqList([]);
      }
    }
  };

  const handleWorkOrderChange = (e) => {
    const val = e.target.value;
    setSelectedWorkOrder(val);
    if (val) {
      const selectedWo = workOrders.find((w) => String(w._id) === String(val));
      const autoProjId = selectedWo?.project?._id || selectedWo?.project;
      if (autoProjId) {
        setForm((prev) => ({ ...prev, project: String(autoProjId) }));
      }
    }
  };

  useEffect(() => {
    setItems([
      {
        boqRefLineItem: "",
        itemName: "",
        quantityRequested: "",
        unit: "",
        remarks: "",
        remaining: 0,
        subItems: [],
      },
    ]);
  }, [selectedWorkOrder]);

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    if (projectFromUrl) {
      setForm((prev) => ({
        ...prev,
        project: projectFromUrl,
      }));
    }
  }, [projectFromUrl]);

  useEffect(() => {
    if (workOrderFromUrl) {
      setSelectedWorkOrder(workOrderFromUrl);
    }
  }, [workOrderFromUrl]);

  const fetchUnits = async () => {
    try {
      const res = await api.get("/boq-unit-types");
      if (res.data.success) {
        setUnits(res.data.data);
      }
    } catch (error) {
      swal("Error", "Failed to fetch units", "error");
    }
  };

  const [projects, setProjects] = useState([]);
  const [boqList, setBoqList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Multiple Items Array
  const [items, setItems] = useState([]);
  // NON-BILLABLE / MANUAL ITEMS
  const [manualItems, setManualItems] = useState([]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await api.get("/projects?limit=1000");
      if (res.data.success) {
        setProjects(res.data.data);
      }
    } catch (error) {
      swal("Error", "Failed to fetch projects", "error");
    }
  };

  // Fetch BOQ for selected project
  useEffect(() => {
    if (!selectedWorkOrder) {
      setBoqList([]);
      return;
    }

    const fetchBOQ = async () => {
      try {
        const res = await api.get(`/work-orders/${selectedWorkOrder}/boq`);
        if (res.data.success) {
          setBoqList(res.data.data);
        }
      } catch (err) {
        swal("Error", "Failed to fetch BOQ items", "error");
      }
    };

    fetchBOQ();
  }, [selectedWorkOrder]);

  // Check if BOQ already selected in another item
  const isBOQSelected = (boqId) => {
    return items.some((it) => it.boqRefLineItem === boqId);
  };

  // Handle BOQ Selection
  const handleBOQSelect = async (index, boqId) => {
    const selected = boqList.find((b) => b._id === boqId);
    if (!selected) return;

    try {
      const res = await api.get(
        `/indents/by-boq/${boqId}?workOrder=${selectedWorkOrder}`,
      );
      const used = res.data.usedQty || 0;
      const remainingQty = selected.quantity - used;

      let updated = [...items];
      updated[index] = {
        ...updated[index],
        boqRefLineItem: boqId,
        itemName: `[WLI-${selected.wlino || "-"}] ${selected.itemName || selected.description}`,
        unit: selected.unit,
        remaining: remainingQty,
      };

      if (remainingQty <= 0)
        swal("Warning", "This BOQ item is fully consumed", "warning");

      setItems(updated);
    } catch (err) {
      swal("Error", "Failed to fetch remaining qty", "error");
    }
  };

  // Update item
  const updateItem = (index, field, value) => {
    let updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  // Handle quantity
  const updateQuantity = (index, qty) => {
    qty = Number(qty);
    let updated = [...items];
    const remaining = updated[index].remaining;

    if (remaining > 0 && qty > remaining) {
      swal({
        title: "Quantity Exceeded",
        text: `Remaining: ${remaining}\nEntered: ${qty}\nProceed anyway?`,
        icon: "warning",
        buttons: ["Cancel", "Proceed"],
      }).then((ok) => {
        if (ok) {
          updated[index].quantityRequested = qty;
          setItems(updated);
        }
      });
      return;
    }

    updated[index].quantityRequested = qty;
    setItems(updated);
  };

  // Add new item
  const addNewItem = () => {
    setItems([
      ...items,
      {
        boqRefLineItem: "",
        itemName: "",
        quantityRequested: "",
        unit: "pcs",
        remarks: "",
        remaining: 0,
        subItems: [],
      },
    ]);
  };

  // Remove item
  const removeItem = (index) => {
    let updated = items.filter((_, i) => i !== index);
    setItems(updated);
  };

  // Add Manual Item
  const addManualItem = () => {
    setManualItems([
      ...manualItems,
      {
        itemName: "",
        quantityRequested: "",
        unit: "pcs",
        remarks: "",
        subItems: [],
      },
    ]);
  };

  // Remove Manual Item
  const removeManualItem = (index) => {
    setManualItems(manualItems.filter((_, i) => i !== index));
  };

  // Update Manual Item
  const updateManualItem = (index, field, value) => {
    const updated = [...manualItems];
    updated[index][field] = value;
    setManualItems(updated);
  };

  // ==========================================
  // SUB-ITEM HANDLERS
  // ==========================================
  const addSubItem = (itemIndex, isManual = false) => {
    if (isManual) {
      const updated = [...manualItems];
      const parentUnit = updated[itemIndex]?.unit || "pcs";
      updated[itemIndex].subItems = [
        ...(updated[itemIndex].subItems || []),
        { name: "", quantity: "", unit: parentUnit, remarks: "" },
      ];
      setManualItems(updated);
    } else {
      const updated = [...items];
      const parentUnit = updated[itemIndex]?.unit || "pcs";
      updated[itemIndex].subItems = [
        ...(updated[itemIndex].subItems || []),
        { name: "", quantity: "", unit: parentUnit, remarks: "" },
      ];
      setItems(updated);
    }
  };

  const removeSubItem = (itemIndex, subIndex, isManual = false) => {
    if (isManual) {
      const updated = [...manualItems];
      updated[itemIndex].subItems = (updated[itemIndex].subItems || []).filter(
        (_, idx) => idx !== subIndex,
      );
      setManualItems(updated);
    } else {
      const updated = [...items];
      updated[itemIndex].subItems = (updated[itemIndex].subItems || []).filter(
        (_, idx) => idx !== subIndex,
      );
      setItems(updated);
    }
  };

  const updateSubItem = (itemIndex, subIndex, field, value, isManual = false) => {
    if (isManual) {
      const updated = [...manualItems];
      const subList = [...(updated[itemIndex].subItems || [])];
      subList[subIndex] = { ...subList[subIndex], [field]: value };
      updated[itemIndex].subItems = subList;
      setManualItems(updated);
    } else {
      const updated = [...items];
      const subList = [...(updated[itemIndex].subItems || [])];
      subList[subIndex] = { ...subList[subIndex], [field]: value };
      updated[itemIndex].subItems = subList;
      setItems(updated);
    }
  };

  const [showSignModal, setShowSignModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate: if any BOQ item is selected, workOrder is required
    const hasBOQItems = items.some((i) => i.boqRefLineItem);
    if (hasBOQItems && !selectedWorkOrder) {
      swal(
        "Error",
        "Work Order is required when BOQ items are selected",
        "error",
      );
      return;
    }

    // Count valid items
    const validBOQItems = items.filter(
      (i) => i.itemName && i.quantityRequested,
    ).length;
    const validManualItems = manualItems.filter(
      (i) => i.itemName && i.quantityRequested,
    ).length;
    const totalItems = validBOQItems + validManualItems;

    if (totalItems === 0) {
      swal("Error", "Please add at least one item (BOQ or Manual)", "error");
      return;
    }

    const payload = {
      ...form,
      workOrder: selectedWorkOrder || null,
      items: [
        // Only include BOQ items that have been filled
        ...items
          .filter((i) => i.itemName && i.quantityRequested)
          .map((i) => ({
            boqRefLineItem: i.boqRefLineItem || null,
            itemName: i.itemName,
            quantityRequested: i.quantityRequested,
            unit: i.unit || "pcs",
            remarks: i.remarks,
            isNonBillable: false,
            subItems: (i.subItems || [])
              .filter((s) => s && s.name && s.quantity)
              .map((s) => ({
                name: s.name.trim(),
                quantity: Number(s.quantity),
                remarks: s.remarks || "",
                unit: s.unit || i.unit || "pcs",
              })),
          })),

        // Include all manual items that have been filled
        ...manualItems
          .filter((i) => i.itemName && i.quantityRequested)
          .map((i) => ({
            boqRefLineItem: null,
            itemName: i.itemName,
            quantityRequested: i.quantityRequested,
            unit: i.unit || "pcs",
            remarks: i.remarks,
            isNonBillable: true,
            subItems: (i.subItems || [])
              .filter((s) => s && s.name && s.quantity)
              .map((s) => ({
                name: s.name.trim(),
                quantity: Number(s.quantity),
                remarks: s.remarks || "",
                unit: s.unit || i.unit || "pcs",
              })),
          })),
      ],
    };

    setPendingPayload(payload);
    setShowSignModal(true);
  };

  const handleSaveSignature = async (file, _stamp, signRemarks) => {
    setShowSignModal(false);
    if (!pendingPayload) return;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("indentNumber", pendingPayload.indentNumber || "");
      formData.append("project", pendingPayload.project || "");
      formData.append("workOrder", pendingPayload.workOrder || "");
      formData.append("status", pendingPayload.status || "Pending");
      formData.append("remarks", signRemarks || pendingPayload.remarks || "");
      formData.append("requiredByDate", pendingPayload.requiredByDate || "");
      formData.append("items", JSON.stringify(pendingPayload.items || []));
      if (file) {
        formData.append("requestedBySignature", file);
      }

      const response = await api.post("/indents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        swal("Success", "Indent created successfully!", "success");

        const backUrl =
          projectFromUrl || workOrderFromUrl
            ? `/indent?${projectFromUrl ? `project=${projectFromUrl}&` : ""}${
                workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""
              }`
            : "/indent";

        navigate(backUrl);
      }
    } catch (error) {
      swal(
        "Error",
        error.response?.data?.message || "Failed to create indent",
        "error",
      );
    } finally {
      setLoading(false);
      setPendingPayload(null);
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Add Indent" motherMenu="Procurement" />
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Card.Title>Add Material Indent (Multiple Items)</Card.Title>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  navigate(
                    workOrderFromUrl
                      ? `/indent?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                      : "/indent",
                  )
                }
              >
                Back
              </button>
            </Card.Header>

            <Card.Body>
              <form onSubmit={handleSubmit}>
                {/* INDENT NUMBER */}
                <div className="mb-3">
                  <label>Indent Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.indentNumber}
                    onChange={(e) =>
                      setForm({ ...form, indentNumber: e.target.value })
                    }
                    placeholder="Leave blank for auto-generation"
                  />
                </div>

                {/* PROJECT */}
                <div className="mb-3">
                  <label>Project *</label>
                  <select
                    className="form-select"
                    value={form.project}
                    onChange={handleProjectChange}
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.projectCode
                          ? `${p.projectCode} – ${p.projectName}`
                          : p.projectName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* WORK ORDER */}
                <div className="mb-3">
                  <label>Work Order *</label>
                  <select
                    className="form-select"
                    value={selectedWorkOrder}
                    onChange={handleWorkOrderChange}
                  >
                    <option value="">Select Work Order</option>

                    {filteredWorkOrders.map((wo) => (
                      <option key={wo._id} value={wo._id}>
                        {wo.woNo}
                        {wo.label ? ` – ${wo.label}` : ""}
                        {!form.project && (wo.project?.projectName || wo.project?.name) ? ` (${wo.project.projectName || wo.project.name})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* MULTIPLE ITEMS */}
                <h4 className="mt-4 mb-3">Indent Items</h4>

                {items.length === 0 && (
                  <div className="alert alert-secondary">
                    No BOQ items added yet. Click "Add Another Item" to add BOQ
                    items or add manual items below.
                  </div>
                )}

                {items.map((item, index) => (
                  <div key={index} className="border rounded p-3 mb-3 bg-white shadow-sm">
                    <div className="row">
                      {/* BOQ Select */}
                      <div className="col-md-6 mb-3">
                        <label>BOQ Item</label>

                        <Select
                          placeholder="Search & select BOQ..."
                          isClearable
                          options={boqList.map((b) => ({
                            value: b._id,
                            label: `[WLI-${b.wlino || "-"}] ${b.itemName || b.description} — ${b.quantity} ${b.unit}`,
                            isDisabled:
                              isBOQSelected(b._id) &&
                              item.boqRefLineItem !== b._id,
                          }))}
                          value={
                            boqList
                              .filter((b) => b._id === item.boqRefLineItem)
                              .map((b) => ({
                                value: b._id,
                                label: `[WLI-${b.wlino || "-"}] ${b.itemName || b.description} — ${b.quantity} ${b.unit}`,
                              }))[0] || null
                          }
                          onChange={(selected) => {
                            if (selected) {
                              handleBOQSelect(index, selected.value);
                            } else {
                              handleBOQSelect(index, "");
                            }
                          }}
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "38px",
                            }),
                          }}
                        />

                        {item.boqRefLineItem && (
                          <p className="text-primary mt-1 mb-0" style={{ fontSize: "0.85rem" }}>
                            Remaining BOQ Qty: <b>{item.remaining}</b>
                          </p>
                        )}
                      </div>

                      {/* Item Name */}
                      <div className="col-md-6 mb-3">
                        <label>Item Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.itemName}
                          onChange={(e) =>
                            updateItem(index, "itemName", e.target.value)
                          }
                          disabled={item.boqRefLineItem ? true : false}
                        />
                      </div>

                      {/* Quantity */}
                      <div className="col-md-4 mb-3">
                        <label>Quantity</label>
                        <input
                          type="number"
                          className="form-control"
                          value={item.quantityRequested}
                          onChange={(e) =>
                            updateQuantity(index, e.target.value)
                          }
                        />
                      </div>

                      {/* Unit */}
                      <div className="col-md-4 mb-3">
                        <label>UOM</label>

                        {item.boqRefLineItem ? (
                          <input
                            type="text"
                            className="form-control"
                            value={item.unit}
                            disabled
                          />
                        ) : (
                          <select
                            className="form-select"
                            value={item.unit}
                            onChange={(e) =>
                              updateItem(index, "unit", e.target.value)
                            }
                          >
                            <option value="">Select UOM</option>
                            {units.map((u) => (
                              <option key={u._id} value={u.name}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Remarks */}
                      <div className="col-md-4 mb-3">
                        <label>Remarks</label>
                        <input
                          className="form-control"
                          value={item.remarks}
                          onChange={(e) =>
                            updateItem(index, "remarks", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* =========================================
                        SUB ITEMS SECTION (BOQ ITEM)
                    ========================================= */}
                    <div className="mt-2 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold text-primary" style={{ fontSize: "0.88rem" }}>
                          <i className="fa fa-list-ul me-1"></i> Sub-Items Breakdown (Optional)
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm py-1 px-2"
                          onClick={() => addSubItem(index, false)}
                        >
                          + Add Sub-Item
                        </button>
                      </div>

                      {item.subItems && item.subItems.length > 0 ? (
                        <div className="table-responsive bg-light p-2 rounded mb-2">
                          <table className="table table-sm table-bordered mb-0 bg-white" style={{ fontSize: "0.85rem" }}>
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: "38%" }}>Sub-Item Name *</th>
                              <th style={{ width: "18%" }}>Quantity *</th>
                              <th style={{ width: "12%" }} className="text-center">UOM</th>
                              <th style={{ width: "27%" }}>Remarks</th>
                                <th style={{ width: "5%" }} className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.subItems.map((sub, sIdx) => (
                                <tr key={sIdx}>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="Sub-item name / specification"
                                      value={sub.name || ""}
                                      onChange={(e) =>
                                        updateSubItem(index, sIdx, "name", e.target.value, false)
                                      }
                                      required
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      placeholder="Quantity"
                                      value={sub.quantity || ""}
                                      onChange={(e) =>
                                        updateSubItem(index, sIdx, "quantity", e.target.value, false)
                                      }
                                      required
                                    />
                                  </td>
                                  <td>
                                    <select
                                      className="form-select form-select-sm"
                                      value={sub.unit || item.unit || ""}
                                      onChange={(e) =>
                                        updateSubItem(index, sIdx, "unit", e.target.value, false)
                                      }
                                    >
                                      <option value="">Select UOM</option>
                                      {item.unit && !units.some((u) => u.name === item.unit) && (
                                        <option value={item.unit}>{item.unit}</option>
                                      )}
                                      {units.map((u) => (
                                        <option key={u._id} value={u.name}>
                                          {u.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="Remarks"
                                      value={sub.remarks || ""}
                                      onChange={(e) =>
                                        updateSubItem(index, sIdx, "remarks", e.target.value, false)
                                      }
                                    />
                                  </td>
                                  <td className="text-center align-middle">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm py-0 px-2"
                                      onClick={() => removeSubItem(index, sIdx, false)}
                                      title="Remove Sub-item"
                                    >
                                      &times;
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <small className="text-muted fst-italic d-block mb-2">
                          No sub-items added. Click "+ Add Sub-Item" to provide item breakdown.
                        </small>
                      )}
                    </div>

                    {items.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm mt-2"
                        onClick={() => removeItem(index)}
                      >
                        Remove Item
                      </button>
                    )}
                  </div>
                ))}

                {/* Add Item */}
                <button
                  type="button"
                  className="btn btn-success mb-4"
                  onClick={addNewItem}
                >
                  + Add Another Item
                </button>

                {/* =========================================
                    NON-BILLABLE / MANUAL ITEMS SECTION
                ========================================= */}
                <h4 className="mt-4 mb-3 text-warning">
                  Non-Billable / Manual Items
                </h4>

                {manualItems.map((item, index) => (
                  <div key={index} className="border rounded p-3 mb-3 bg-white shadow-sm">
                    <div className="row">
                      {/* Item Name */}
                      <div className="col-md-4 mb-3">
                        <label>Item Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.itemName}
                          onChange={(e) =>
                            updateManualItem(index, "itemName", e.target.value)
                          }
                          required
                        />
                      </div>

                      {/* Quantity */}
                      <div className="col-md-2 mb-3">
                        <label>Quantity *</label>
                        <input
                          type="number"
                          className="form-control"
                          value={item.quantityRequested}
                          onChange={(e) =>
                            updateManualItem(
                              index,
                              "quantityRequested",
                              e.target.value,
                            )
                          }
                          required
                        />
                      </div>

                      {/* Unit */}
                      <div className="col-md-2 mb-3">
                        <label>UOM</label>
                        <select
                          className="form-select"
                          value={item.unit}
                          onChange={(e) =>
                            updateManualItem(index, "unit", e.target.value)
                          }
                        >
                          <option value="">Select UOM</option>
                          {units.map((u) => (
                            <option key={u._id} value={u.name}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Remarks */}
                      <div className="col-md-4 mb-3">
                        <label>Remarks</label>
                        <input
                          className="form-control"
                          value={item.remarks}
                          onChange={(e) =>
                            updateManualItem(index, "remarks", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* =========================================
                        SUB ITEMS SECTION (MANUAL ITEM)
                    ========================================= */}
                    <div className="mt-2 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold text-warning" style={{ fontSize: "0.88rem" }}>
                          <i className="fa fa-list-ul me-1"></i> Sub-Items Breakdown (Optional)
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-warning btn-sm py-1 px-2 text-dark"
                          onClick={() => addSubItem(index, true)}
                        >
                          + Add Sub-Item
                        </button>
                      </div>

                      {item.subItems && item.subItems.length > 0 ? (
                        <div className="table-responsive bg-light p-2 rounded mb-2">
                          <table className="table table-sm table-bordered mb-0 bg-white" style={{ fontSize: "0.85rem" }}>
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: "38%" }}>Sub-Item Name *</th>
                              <th style={{ width: "18%" }}>Quantity *</th>
                              <th style={{ width: "12%" }} className="text-center">UOM</th>
                              <th style={{ width: "27%" }}>Remarks</th>
                                <th style={{ width: "5%" }} className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.subItems.map((sub, sIdx) => (
                                <tr key={sIdx}>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="Sub-item name / specification"
                                      value={sub.name || ""}
                                      onChange={(e) =>
                                        updateSubItem(index, sIdx, "name", e.target.value, true)
                                      }
                                      required
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      placeholder="Quantity"
                                      value={sub.quantity || ""}
                                      onChange={(e) =>
                                        updateSubItem(index, sIdx, "quantity", e.target.value, true)
                                      }
                                      required
                                    />
                                  </td>
                                  <td>
                                    <select
                                      className="form-select form-select-sm"
                                      value={sub.unit || item.unit || ""}
                                      onChange={(e) =>
                                        updateSubItem(index, sIdx, "unit", e.target.value, true)
                                      }
                                    >
                                      <option value="">Select UOM</option>
                                      {item.unit && !units.some((u) => u.name === item.unit) && (
                                        <option value={item.unit}>{item.unit}</option>
                                      )}
                                      {units.map((u) => (
                                        <option key={u._id} value={u.name}>
                                          {u.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="Remarks"
                                      value={sub.remarks || ""}
                                      onChange={(e) =>
                                        updateSubItem(index, sIdx, "remarks", e.target.value, true)
                                      }
                                    />
                                  </td>
                                  <td className="text-center align-middle">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm py-0 px-2"
                                      onClick={() => removeSubItem(index, sIdx, true)}
                                      title="Remove Sub-item"
                                    >
                                      &times;
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <small className="text-muted fst-italic d-block mb-2">
                          No sub-items added. Click "+ Add Sub-Item" to provide item breakdown.
                        </small>
                      )}
                    </div>

                    <button
                      type="button"
                      className="btn btn-danger btn-sm mt-2"
                      onClick={() => removeManualItem(index)}
                    >
                      Remove Item
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-warning mb-4"
                  onClick={addManualItem}
                >
                  + Add Manual Item
                </button>

                {/* Required Date */}
                <div className="mb-3">
                  <label>Required By Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.requiredByDate}
                    onChange={(e) =>
                      setForm({ ...form, requiredByDate: e.target.value })
                    }
                  />
                </div>

                {/* Status */}
                <div className="mb-3">
                  <label>Status</label>
                  <select
                    className="form-select"
                    value={form.status}
                    onChange={(e) =>
                      setForm({ ...form, status: e.target.value })
                    }
                  >
                    <option>Pending</option>
                    <option>Procured</option>
                    <option>Partially Procured</option>
                  </select>
                </div>

                {/* Remarks */}
                <div className="mb-3">
                  <label>Overall Remarks</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={form.remarks}
                    onChange={(e) =>
                      setForm({ ...form, remarks: e.target.value })
                    }
                  ></textarea>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Indent"}
                </button>

                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() =>
                    navigate(
                      workOrderFromUrl
                        ? `/indent?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                        : "/indent",
                    )
                  }
                >
                  Cancel
                </button>
              </form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SignatureModal
        show={showSignModal}
        onHide={() => {
          setShowSignModal(false);
          setPendingPayload(null);
          setLoading(false);
        }}
        onSave={handleSaveSignature}
        title="Requested By Signature for Material Indent"
      />
    </Fragment>
  );
};

export default IndentAdd;
